import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import responseHandler from "responseHandler";
import { connect } from "react-redux";
import { getEmployeesByCourse, updateEmployeeCourse } from "requests/employeeCourse";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DATE, DATA_TYPE_NUMBER, DATA_TYPE_BUTTON } from "typeConstants/dataTypes";
import { FILTER_TYPE_NONE } from "components/Shared/MGrid.tsx";
import { MGrid, MSpinner, MCard } from "components/Shared";
import { dateToStringYy } from "components/Shared/Formatters";
import { EMPLOYEE_COURSE_EDIT } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { getEditEmployeeCourseCardFields } from "components/Employee/EmployeeCourses";
import { defaultHeightStyle } from "styles/sharedStyles";

const copyButton = (row) => row.fileName && <i className="md-icon material-icons">content_copy</i>;

const colDefs = [
  { name: "First Name", dataSource: "employee.firstName" },
  { name: "Last Name", dataSource: "employee.lastName" },
  { name: "Role", dataSource: "employee.role.name" },
  { name: "Email", dataSource: "employee.emailAddress" },
  { name: "Phone", dataSource: "employee.phoneNumber" },
  { name: "Supervisor", dataSource: "employee.supervisorName" },
  { name: "Created Date", dataSource: "createDate", width: 68, dataType: DATA_TYPE_DATE },
  { name: "Completed Date", dataSource: "completedDate", width: 75, dataType: DATA_TYPE_DATE },
  { name: "Due Date", dataSource: "dueDate", width: 77, dataType: DATA_TYPE_DATE },
  { name: "Duration (Minutes)", width: 140, dataSource: "durationMinutes", dataType: DATA_TYPE_NUMBER },
  { name: "Notification Enabled", width: 147, dataSource: "notificationEnabled", dataType: DATA_TYPE_BOOLEAN },
  { name: "Score", dataSource: "score", width: 57, dataType: DATA_TYPE_NUMBER },
  { name: "Active", dataSource: "isActive", width: 60, dataType: DATA_TYPE_BOOLEAN },
  { name: "File", dataSource: copyButton, exportValue: "fileName", width: 57, dataType: DATA_TYPE_BUTTON, tooltip: () => "Click to copy file location to clipboard", filterType: FILTER_TYPE_NONE }
].map(c => ({ key: c.name, ...c }));

const CourseEmployees = ({ 
    courseId, 
    canEditEmployeeCourse, 
    makeErrorToast,
    makeSuccessToast,
    cardSx,
  }) => {
  const [employeeCourses, setEmployeeCourses] = useState();
  const [selectedGridIndex, setSelectedGridIndex] = useState();
  const [selectedEmployeeCourse, setSelectedEmployeeCourse] = useState(null);
  const [isEditingEmployeeCourse, setIsEditingEmployeeCourse] = useState(false);
  const [employeeCourseCompletion, setEmployeeCourseCompletion] = useState(null);

  const mounted = useRef(true);
  const gridControlMethods = useRef();

  const getEmployeesByCourseWrapped = () => getEmployeesByCourse(
    courseId,
    courses => setEmployeeCourses(courses.sort((c1, c2) => c1.employee.lastName.localeCompare(c2.employee.lastName))),
    mounted);

  useEffect(() => {
    getEmployeesByCourseWrapped();
  }, [courseId]);

  useEffect(() => () => mounted.current = false, []);

  const onCourseEmployeeSelected = i => {
    setSelectedGridIndex(i);
    setSelectedEmployeeCourse(employeeCourses[i]);
  };

  const copyToClipboard = (index) => {
    navigator.clipboard.writeText(employeeCourses[index].fileName);
    makeSuccessToast("Path copied to clipboard");
  };

  const onEditCourseClicked = () => {
    setEmployeeCourseCompletion({
        id: selectedEmployeeCourse.id,
        completedDate: dateToStringYy(selectedEmployeeCourse?.completedDate),
        durationMinutes: selectedEmployeeCourse?.durationMinutes,
        dueDate: selectedEmployeeCourse?.dueDate,
        score: selectedEmployeeCourse?.score,
        notificationEnabled: selectedEmployeeCourse?.notificationEnabled,
      });
    setIsEditingEmployeeCourse(true);
  };

  const onEditCancelled = () => {
    setIsEditingEmployeeCourse(false);
    setEmployeeCourseCompletion(null);
  };

  const onEditFinished = () => {
    if (!employeeCourseCompletion) return;

    const todayDate = new Date(new Date().toDateString());
    if (employeeCourseCompletion.dueDate < todayDate) {
      makeErrorToast("Due Date must be in the future");
      return;
    }

    let score = employeeCourseCompletion.score;
    if (score < 0 || score > 100) {
      makeErrorToast("Score has to be between 0 and 100");
      return;
    }
    setEmployeeCourses(null);
    setSelectedGridIndex(null);
    setSelectedEmployeeCourse(null);
    setIsEditingEmployeeCourse(false);
    
    updateEmployeeCourse(employeeCourseCompletion, getEmployeesByCourseWrapped, mounted);
    setEmployeeCourseCompletion(null);
  };

  if (!employeeCourses) return <MSpinner/>;

  let courseName = employeeCourses.length > 0 && employeeCourses[0].course.name;
  
  let cardActions = [
    !isEditingEmployeeCourse && { icon: "cloud_download", helpText: "Download", onClick: () => gridControlMethods.current.downloadGrid() }, //this needs to be wrapped in a function because the .current is null initially, wrapping it in a function uses its new value after the callback is called
    isEditingEmployeeCourse && { icon: "check", helpText: "Apply Changes", onClick: onEditFinished },
    isEditingEmployeeCourse && { icon: "close", helpText: "Cancel Editing", onClick: onEditCancelled },
    !isEditingEmployeeCourse && canEditEmployeeCourse && selectedEmployeeCourse && { icon: "edit", helpText: "Edit Record", onClick: onEditCourseClicked },
  ].filter(c => c);

  const editEmployeeCourseFields = isEditingEmployeeCourse && getEditEmployeeCourseCardFields(selectedEmployeeCourse);

  return (
    <MCard
      title={isEditingEmployeeCourse ? `Editing course ${selectedEmployeeCourse?.course.name} for employee ${selectedEmployeeCourse?.employee.firstLastName}` : "Course Employees"}
      className="course-employees"
      actions={cardActions}
      fields={isEditingEmployeeCourse && editEmployeeCourseFields}
      key={isEditingEmployeeCourse ? "editing-course" : "viewing-courses"} //change the key so the card remounts because we change some props that the card doesnt support changing on the fly
      onlyEditable={isEditingEmployeeCourse}
      onEditFinished={isEditingEmployeeCourse ? ((_, o) => setEmployeeCourseCompletion({ ...employeeCourseCompletion, ...o })) : undefined}
      sx={{ ...defaultHeightStyle, ...cardSx }}
    >
      {!isEditingEmployeeCourse &&
      <MGrid
        columnDefinitions={colDefs}
        data={employeeCourses}
        onRowClick={i => onCourseEmployeeSelected(i)}
        onCellButtonClick={copyToClipboard}
        rowLastClicked={selectedGridIndex}
        getControlMethods={cm => gridControlMethods.current = cm}
        downloadFileName={`Employees assigned to ${courseName}`}
      />}
    </MCard>);
};

CourseEmployees.propTypes = {
  courseId: PropTypes.number,
  makeErrorToast: PropTypes.func.isRequired,
  makeSuccessToast: PropTypes.func.isRequired,
  canEditEmployeeCourse: PropTypes.bool.isRequired,
  cardSx: PropTypes.object,
};

const mapStateToProps = state => ({
  canEditEmployeeCourse: userHasRight(EMPLOYEE_COURSE_EDIT)(state),
});

const mapDispatchToProps = (dispatch) => ({
  makeErrorToast(text) {
    responseHandler.customToast(text, "error", dispatch);
  },
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseEmployees);